import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getProgramList } from '../../utils/adminData';
import { groupProgramsByCategory } from '../../data/programsConfig';

function ProgramCard({ p }) {
  const displayName = p.displayName ?? p.name ?? p.id;
  return (
    <div className="admin-catalog-card admin-catalog-card--program">
      <div className="admin-catalog-card-body">
        <div className="admin-catalog-card-content">
          <div className="admin-catalog-card-header">
            <strong className="admin-catalog-name">{displayName}</strong>
            <span className="admin-catalog-id text-muted text-sm">{p.id}</span>
            {p.type === 'template' && (
              <span className="admin-catalog-program-badge" title="Runtime template">
                template
              </span>
            )}
          </div>
        </div>
        <div className="admin-catalog-edit-row">
          <Link
            to={`/admin/programs/${encodeURIComponent(p.id)}/edit`}
            className="btn btn--ghost btn--sm"
            aria-label="Edit"
          >
            Edit
          </Link>
        </div>
      </div>
    </div>
  );
}

function AdminDashboard() {
  const [programList, setProgramList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleRetry = () => {
    setListError(null);
    setRefreshTrigger((c) => c + 1);
  };

  useEffect(() => {
    let cancelled = false;
    setListLoading(true);
    setListError(null);
    getProgramList()
      .then((dbList) => {
        if (!cancelled) {
          setProgramList(Array.isArray(dbList) ? dbList : []);
          setListError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setProgramList([]);
          setListError(err?.message || 'Failed to load programs from database. Check connection and permissions.');
        }
      })
      .finally(() => { if (!cancelled) setListLoading(false); });
    return () => { cancelled = true; };
  }, [refreshTrigger]);

  const published = programList.filter((p) => p.published === true);
  const draft = programList.filter((p) => p.published !== true);
  const publishedByCategory = groupProgramsByCategory(published);
  const draftByCategory = groupProgramsByCategory(draft);

  return (
    <div className="admin-content">
      <div className="admin-content-panel">
        {listLoading ? (
          <div className="admin-content-loading" role="status">
            <p className="text-secondary">Loading programs from database…</p>
          </div>
        ) : listError ? (
          <div className="admin-content-loading" role="alert">
            <p style={{ color: 'var(--color-error)', fontWeight: 600 }}>{listError}</p>
            <p className="text-secondary text-sm" style={{ marginTop: 'var(--space-2)' }}>
              Check your connection and Firestore rules, then retry.
            </p>
            <button type="button" className="btn btn--secondary" style={{ marginTop: 'var(--space-4)' }} onClick={handleRetry}>
              Retry
            </button>
          </div>
        ) : programList.length === 0 ? (
          <div className="admin-content-loading" role="status">
            <p className="text-secondary">No programs in database.</p>
            <p className="text-secondary text-sm" style={{ marginTop: 'var(--space-2)' }}>
              Create a program to get started.
            </p>
            <div style={{ marginTop: 'var(--space-4)', display: 'flex', gap: 'var(--space-3)' }}>
              <Link to="/admin/programs/new" className="btn btn--primary">
                Create new program
              </Link>
            </div>
          </div>
        ) : (
          <div className="admin-catalog admin-catalog--programs">
            <div className="admin-content-top">
              <div className="admin-content-top-heading">
                <h2 className="admin-content-section-title">Programs</h2>
                <p className="admin-content-section-desc">
                  Manage programs — macrocycles, mesocycles, and workout assignments. Edit or add programs.
                </p>
              </div>
              <div className="admin-content-top-actions">
                <Link
                  to="/admin/programs/new"
                  className="admin-content-add-btn"
                  title="Create a new program"
                >
                  <span className="admin-content-add-icon">+</span>
                  Add program
                </Link>
              </div>
            </div>

            <div className="admin-programs-sections">
              <section className="admin-programs-section" aria-labelledby="programs-published-heading">
                <h3 id="programs-published-heading" className="admin-programs-section-title">
                  Ready for use
                </h3>
                <p className="admin-programs-section-desc text-secondary text-sm">
                  Published programs available to athletes.
                </p>
                {publishedByCategory.length > 0 ? (
                  publishedByCategory.map((group) => (
                    <div key={group.category} className="admin-programs-category">
                      <h4 className="admin-programs-category-title">{group.categoryLabel}</h4>
                      <div className="admin-catalog-list admin-catalog-list--programs">
                        {group.programs.map((p) => <ProgramCard key={p.id} p={p} />)}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="admin-programs-empty text-muted text-sm">No published programs yet.</p>
                )}
              </section>

              <section className="admin-programs-section" aria-labelledby="programs-draft-heading">
                <h3 id="programs-draft-heading" className="admin-programs-section-title">
                  Needs to be built
                </h3>
                <p className="admin-programs-section-desc text-secondary text-sm">
                  Draft programs — complete and publish when ready.
                </p>
                {draftByCategory.length > 0 ? (
                  draftByCategory.map((group) => (
                    <div key={group.category} className="admin-programs-category">
                      <h4 className="admin-programs-category-title">{group.categoryLabel}</h4>
                      <div className="admin-catalog-list admin-catalog-list--programs">
                        {group.programs.map((p) => <ProgramCard key={p.id} p={p} />)}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="admin-programs-empty text-muted text-sm">All programs are published.</p>
                )}
              </section>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
